import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { View } from 'react-native';
import AmountSelectors from 'design-system/AmountSelectors';
import Button from 'design-system/Button';
import MoneyInput from 'design-system/MoneyInput';
import Spacer from 'design-system/Spacer';
import { rem } from 'design-system/values';
import useColors from 'hooks/useColors';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useStyles from 'hooks/useStyles';
import { getBalance } from 'utils/api/getBalance';
import createStyleSheets from 'utils/createStyleSheets';
import { AppView } from 'design-system';
import InfoCard from 'design-system/InfoCard';
import StatusBar from 'design-system/StatusBar';
import Text from 'design-system/Text';
import { DEFAULT_VRP_PERIOD_ALIGNMENT, DEFAULT_VRP_PERIOD_FREQUENCY, MAX_PER_TRANSFER_LIMIT, } from 'features/pre-approved-transfers/constants';
import { selectHasPreApprovedTransfersFeature } from 'features/pre-approved-transfers/selectors';
import { getMinimumVRPPeriodLimit } from 'features/pre-approved-transfers/utils';
import useHeaderHeight from 'hooks/useHeaderHeight';
import { selectFeatureFlag } from 'reducers/selectors';
import { useAppSelector, useAppStore } from 'store/hooks';
import { MIN_TOP_UP_AMOUNT } from '../constants';
import createTopUpErrorHandler from '../utils/createTopUpErrorHandler';
const strings = {
    navTitle: 'Deposit',
    button: 'Continue',
    hint: 'Minimum amount is £1',
};
const AMOUNT_SELECTORS = [100, 500, 1000];
const TopUpScreen = ({ route, navigation: { navigateDeprecated, setOptions } }) => {
    const moneyInputRef = useRef(null);
    const minDeposit = useAppSelector((store) => selectFeatureFlag(store, 'min_deposit')?.extra?.minDeposit);
    const minTopUpAmount = typeof minDeposit === 'number' && minDeposit > 1 ? minDeposit : MIN_TOP_UP_AMOUNT;
    const { params } = route;
    const flow = params?.flow;
    const bankAccount = 'consent' in params.from ? params.from.account : params.from;
    const consent = 'consent' in params.from ? params.from.consent : undefined;
    const [primaryBalance] = getBalance(bankAccount);
    const colors = useColors();
    const styles = useStyles(styleSet);
    useLayoutEffect(() => {
        setOptions({
            headerTitle: strings.navTitle,
            headerTintColor: colors.text.primary,
        });
    }, [colors.background.brand, colors.background.light, colors.text.primary, colors.text.whiteOnColor, setOptions]);
    const initialValue = (() => {
        if (primaryBalance) {
            if (primaryBalance.amount >= 3000) {
                return 1000;
            }
            if (primaryBalance.amount >= 2000) {
                return 500;
            }
            if (primaryBalance.amount >= 1000) {
                return 100;
            }
        }
        return 0;
    })();
    const [disabled, setDisabled] = useState(true);
    const amountRef = useRef(0);
    const [showOverPreApproved, setShowOverPreApproved] = useState(false);
    const onAmountChange = useCallback((value) => {
        amountRef.current = value;
        if (value >= minTopUpAmount) {
            setDisabled(false);
        }
        else {
            setDisabled(true);
        }
        if (consent) {
            if (value > consent.perPaymentLimit || value > consent.remainingAllowance) {
                if (value > MAX_PER_TRANSFER_LIMIT) {
                    setShowOverPreApproved('overMaxLimit');
                    return;
                }
                const minimumRequiredVRPPeriodLimit = getMinimumVRPPeriodLimit({
                    createdAt: new Date().toDateString(),
                    periodAlignment: consent.periodAlignment || DEFAULT_VRP_PERIOD_ALIGNMENT,
                    periodicLimitFrequency: consent.periodicLimitFrequency || DEFAULT_VRP_PERIOD_FREQUENCY,
                }, value);
                if (minimumRequiredVRPPeriodLimit > MAX_PER_TRANSFER_LIMIT) {
                    setShowOverPreApproved('overMaxLimit');
                    return;
                }
                setShowOverPreApproved('overMyLimits');
                return;
            }
            setShowOverPreApproved(false);
        }
    }, [consent, minTopUpAmount]);
    const store = useAppStore();
    const onSubmit = useCallback(() => {
        if (bankAccount) {
            const bank = 'consent' in route.params.from ? route.params.from.account : route.params.from;
            const params = {
                flow: flow,
                bankAccount: bank,
                amount: amountRef.current,
                backCount: 3,
                onError: createTopUpErrorHandler(onSubmit),
                reference: 'EMMA TRADING',
                consentId: consent?.id,
            };
            const hasPreApprovedTransfersFeature = selectHasPreApprovedTransfersFeature(store.getState());
            if (hasPreApprovedTransfersFeature) {
                if (!consent && bank.supportsVrp && amountRef.current <= MAX_PER_TRANSFER_LIMIT) {
                    const minimumRequiredVRPPeriodLimit = getMinimumVRPPeriodLimit({
                        createdAt: new Date().toISOString(),
                        periodAlignment: DEFAULT_VRP_PERIOD_ALIGNMENT,
                        periodicLimitFrequency: DEFAULT_VRP_PERIOD_FREQUENCY,
                    }, amountRef.current);
                    if (minimumRequiredVRPPeriodLimit <= MAX_PER_TRANSFER_LIMIT) {
                        // User needs to set up pre-approved transfers for the first time for this account
                        navigateDeprecated('PreApprovedTransferIntro', params);
                        return;
                    }
                }
                if (consent && showOverPreApproved) {
                    // User doesn’t have enough pre-approved allowance left, treat as normal payment
                    navigateDeprecated('InvestCreateTopUp', { ...params, consentId: undefined });
                    return;
                }
                if (consent) {
                    // Using VRP to make the transfer
                    navigateDeprecated('PreApprovedTransferConfirmPayment', { ...params, consent });
                    return;
                }
            }
            // Normal payment with redirect to bank
            navigateDeprecated('InvestCreateTopUp', params);
        }
    }, [bankAccount, consent, flow, route.params.from, showOverPreApproved]);
    const numPadHeader = useMemo(() => (<View style={styles.numPadHeader}>
        <Spacer h={24}/>
        <AmountSelectors amounts={AMOUNT_SELECTORS} moneyInputRef={moneyInputRef}/>

        {showOverPreApproved ? (<>
            <Spacer h={22}/>
            <InfoCard onLight>
              <Text TextThin-14 Secondary>
                {showOverPreApproved === 'overMaxLimit' ? ('This amount exceeds pre-approved limits. You’ll need to approve this payment.') : (<>
                    This amount exceeds your pre-approved limits. You’ll need to approve this payment or{' '}
                    <Text TextThin-14 Primary suppressHighlighting onPress={() => {
                    if (bankAccount) {
                        navigateDeprecated('PreApprovedTransferSetLimits', {
                            bankAccount: bankAccount,
                            amount: amountRef.current,
                            backCount: 3,
                            onError: createTopUpErrorHandler(onSubmit),
                            reference: 'EMMA TRADING',
                            consentId: consent?.id,
                            defaultPerPaymentLimit: consent?.periodicLimitAmount,
                            defaultPeriodicLimitAmount: consent?.perPaymentLimit,
                        });
                    }
                }}>
                      increase limits.
                    </Text>
                  </>)}
              </Text>
            </InfoCard>
          </>) : null}
      </View>), [
        bankAccount,
        consent?.id,
        consent?.perPaymentLimit,
        consent?.periodicLimitAmount,
        onSubmit,
        showOverPreApproved,
        styles.numPadHeader,
    ]);
    const moneyInputColors = useMemo(() => ({
        input: colors.text.primary,
        pinpad: colors.text.primary,
        hintColor: colors.text.secondary,
    }), [colors.text.primary, colors.text.secondary]);
    const headerHeight = useHeaderHeight();
    const paddingTop = useMemo(() => ({
        paddingTop: headerHeight * 0.6,
    }), [headerHeight]);
    const paddingBottom = useMarginBottom('paddingBottom', undefined, styles.container, paddingTop);
    return (<AppView type="primary">
      <View style={paddingBottom}>
        <StatusBar barStyle="dark-content" animated/>
        <MoneyInput flex showMinHint ref={moneyInputRef} min={minTopUpAmount} colors={moneyInputColors} initialValue={initialValue} numPadHeader={numPadHeader} onValueChange={onAmountChange} pinPadStyle={styles.pinPadStyle} inputFieldStyle={styles.inputFieldStyle} restrictAmountsLessThanOne={MIN_TOP_UP_AMOUNT >= 1}/>
        <Spacer h={20}/>
        <View style={styles.buttonContainer}>
          <Button brand disabled={disabled} title={strings.button} onPress={onSubmit}/>
        </View>
      </View>
    </AppView>);
};
const styleSet = createStyleSheets(() => ({
    container: {
        flex: 1,
    },
    buttonContainer: {
        paddingHorizontal: rem(16),
    },
    numPadHeader: {
        flex: 3,
        width: '100%',
    },
    inputFieldStyle: {
        fontSize: rem(64),
        lineHeight: rem(87.3),
    },
    pinPadStyle: {
        marginTop: rem(-10),
    },
}));
export default TopUpScreen;
